import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
    X, CurrencyDollar, ShieldCheck, ChatCircle, UsersThree, Truck, ChatTeardropDots, Plus, Trash, ThumbsUp, Warning,
} from "@phosphor-icons/react";
import { fmtDuration } from "../lib/duration";

/* Unified post-operations data manager for a vessel. Used on:
   - Live vessel detail (during ops)
   - Completed vessel history (after ops)

   Tabs (visible only if the signed-in user can read/write that section):
     · Budget & Costs  (admin/manager/finance)
     · Manpower        (admin/manager)
     · Fleet           (admin/manager/fleet_manager)
     · Safety          (admin/manager/safety)
     · Client feedback (admin/manager/client_liaison)
     · Delay feedback  (admin/manager) */

const fmtZAR = (n) => `R ${Number(n || 0).toLocaleString("en-ZA", { maximumFractionDigits: 0 })}`;
const fmtDate = (iso) => iso ? new Date(iso).toLocaleString("en-GB", { dateStyle: "short", timeStyle: "short" }) : "—";

export default function VesselPostOpModal({ vessel, onClose, onChanged }) {
    const { user } = useAuth();
    const role = user?.role;
    const allTabs = [
        { key: "budget",   label: "Budget & Costs", Icon: CurrencyDollar, accent: "#B36B00", roles: ["admin","finance"] },
        { key: "manpower", label: "Manpower",       Icon: UsersThree,     accent: "#1E40AF", roles: ["admin","manager"] },
        { key: "fleet",    label: "Fleet",          Icon: Truck,          accent: "var(--grindrod-navy)", roles: ["admin","fleet_manager"] },
        { key: "safety",   label: "Safety",         Icon: ShieldCheck,    accent: "#00A344", roles: ["admin","safety"] },
        { key: "client",   label: "Client",         Icon: ChatCircle,     accent: "#7C3AED", roles: ["admin","client_liaison"] },
        { key: "delays",   label: "Delay feedback", Icon: ChatTeardropDots, accent: "var(--grindrod-red)", roles: ["admin","manager"] },
    ];
    const tabs = allTabs.filter((t) => t.roles.includes(role));
    const [tab, setTab] = useState(tabs[0]?.key || "budget");

    if (tabs.length === 0) return null;

    return (
        <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-2 sm:p-4" onClick={onClose}>
            <div className="bg-white w-full max-w-5xl max-h-[95vh] rounded-sm shadow-2xl flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="vessel-postop-modal">
                <div className="g-card-header-dark">
                    <div className="font-display font-black uppercase tracking-wide truncate">{vessel.vessel_name} · Operations data</div>
                    <span className="hidden sm:inline ml-2 text-[10px] text-slate-300 uppercase tracking-wider truncate">{vessel.client_name} · {vessel.status === "completed" ? "Completed" : "Active"}</span>
                    <button onClick={onClose} className="ml-auto text-white text-lg leading-none" data-testid="vessel-postop-close" aria-label="Close">×</button>
                </div>

                {/* Tab bar — horizontally scrollable on mobile */}
                <div className="border-b border-slate-200 bg-slate-50 overflow-x-auto">
                    <div className="flex min-w-max">
                        {tabs.map((t) => {
                            const isActive = t.key === tab;
                            return (
                                <button key={t.key} onClick={() => setTab(t.key)} data-testid={`postop-tab-${t.key}`}
                                        className={`flex items-center gap-2 px-3 sm:px-4 py-2.5 text-[11px] sm:text-xs font-display font-bold uppercase tracking-wide whitespace-nowrap border-b-2 transition-colors ${isActive ? "border-[var(--grindrod-red)] text-[var(--grindrod-navy)] bg-white" : "border-transparent text-slate-500 hover:text-[var(--grindrod-navy)] hover:bg-white/60"}`}>
                                    <span className="w-5 h-5 rounded-sm flex items-center justify-center text-white flex-shrink-0" style={{ background: t.accent }}>
                                        <t.Icon size={11} weight="fill" />
                                    </span>
                                    {t.label}
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div className="flex-1 overflow-auto p-3 sm:p-4 bg-slate-50">
                    {tab === "budget"   && <BudgetCostsTab   vessel={vessel} onChanged={onChanged} role={role} />}
                    {tab === "manpower" && <ManpowerTab      vessel={vessel} onChanged={onChanged} role={role} />}
                    {tab === "fleet"    && <FleetTab         vessel={vessel} onChanged={onChanged} role={role} />}
                    {tab === "safety"   && <SafetyTab        vessel={vessel} onChanged={onChanged} role={role} />}
                    {tab === "client"   && <ClientTab        vessel={vessel} onChanged={onChanged} role={role} />}
                    {tab === "delays"   && <DelayFeedbackTab vessel={vessel} onChanged={onChanged} role={role} />}
                </div>
            </div>
        </div>
    );
}

/* ------------------------------------------------ SHARED UI ------------ */
function SectionCard({ title, accent, children, action }) {
    return (
        <div className="bg-white rounded-sm border border-slate-200 overflow-hidden">
            <div className="px-3 py-2 flex items-center gap-2 border-b border-slate-200" style={{ background: accent || "var(--grindrod-navy)", color: "white" }}>
                <div className="font-display font-bold text-[11px] uppercase tracking-wide flex-1">{title}</div>
                {action}
            </div>
            <div className="p-3">{children}</div>
        </div>
    );
}
function DeleteBtn({ onClick, testid }) {
    return (
        <button onClick={onClick} data-testid={testid} className="inline-flex items-center gap-1 px-2 py-1 rounded-sm border border-[var(--grindrod-red)] text-[var(--grindrod-red)] hover:bg-[var(--grindrod-red)] hover:text-white transition-colors text-[10px] font-display font-bold uppercase tracking-wide">
            <Trash size={10} weight="bold" /> Delete
        </button>
    );
}

/* ------------------------------------------------ BUDGET + COSTS ------------- */
function BudgetCostsTab({ vessel, onChanged, role }) {
    const canEdit = ["admin","finance"].includes(role);
    const [budget, setBudget] = useState(null);
    const [costs, setCosts] = useState({ items: [], total_zar: 0, by_category: {} });
    const [loading, setLoading] = useState(true);
    const [showCost, setShowCost] = useState(false);
    const [showBudget, setShowBudget] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const [b, c] = await Promise.all([
                api.get(`/vessels/${vessel.id}/budget`),
                api.get(`/vessels/${vessel.id}/costs`),
            ]);
            setBudget(b.data || null);
            setCosts(c.data || { items: [], total_zar: 0, by_category: {} });
        } catch (e) { toast.error(formatApiError(e)); }
        setLoading(false);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    const tons = vessel.total_tons_loaded || vessel.tonnage || 0;
    const cpt = tons > 0 ? costs.total_zar / tons : 0;
    const variance = (budget?.total_budget_zar || 0) - costs.total_zar;
    const removeCost = async (cid) => {
        if (!window.confirm("Delete this cost line?")) return;
        try { await api.delete(`/vessels/${vessel.id}/costs/${cid}`); toast.success("Cost removed"); load(); onChanged?.(); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <div className="space-y-3" data-testid="postop-budget-pane">
            <div className="grid sm:grid-cols-4 gap-2">
                <Stat label="Budget"      val={fmtZAR(budget?.total_budget_zar)} accent="#1E40AF" testid="cost-stat-budget" />
                <Stat label="Actual cost" val={fmtZAR(costs.total_zar)}          accent="#B36B00" testid="cost-stat-actual" />
                <Stat label="Variance"    val={fmtZAR(variance)} sub={variance >= 0 ? "under budget" : "over budget"} accent={variance >= 0 ? "var(--grindrod-green)" : "var(--grindrod-red)"} testid="cost-stat-variance" />
                <Stat label="Cost per ton" val={fmtZAR(cpt)} sub={`${Math.round(tons).toLocaleString()} t`} accent="var(--grindrod-navy)" testid="cost-stat-cpt" />
            </div>
            <SectionCard title="Cost line items" accent="#B36B00"
                action={canEdit && (
                    <>
                        <button onClick={() => setShowBudget(true)} data-testid="cost-set-budget" className="px-2 py-1 rounded-sm bg-white/15 hover:bg-white/25 text-[10px] font-display font-bold uppercase tracking-wide">{budget ? "Update budget" : "Set budget"}</button>
                        <button onClick={() => setShowCost(true)} data-testid="cost-add-line" className="px-2 py-1 rounded-sm bg-white text-[#B36B00] hover:bg-amber-50 text-[10px] font-display font-bold uppercase tracking-wide inline-flex items-center gap-1"><Plus size={10} weight="bold" /> Add cost</button>
                    </>
                )}>
                {loading ? <div className="text-center py-6 text-slate-400 text-xs">Loading…</div> :
                 costs.items.length === 0 ? <div className="text-center py-6 text-slate-400 text-xs">No cost lines yet.</div> :
                 <table className="w-full text-xs tabular">
                    <thead className="text-slate-500 uppercase text-[10px]"><tr><th className="text-left p-1.5">Category</th><th className="text-left p-1.5">Description</th><th className="text-right p-1.5">Amount</th><th className="text-left p-1.5">Logged by</th><th className="p-1.5"></th></tr></thead>
                    <tbody>{costs.items.map((c) => (
                        <tr key={c.id} className="border-t border-slate-100" data-testid={`cost-row-${c.id}`}>
                            <td className="p-1.5 capitalize">{(c.category || "").replace(/_/g," ")}</td>
                            <td className="p-1.5 max-w-xs truncate" title={c.description}>{c.description || "—"}</td>
                            <td className="p-1.5 text-right font-semibold">{fmtZAR(c.amount_zar)}</td>
                            <td className="p-1.5 text-slate-500">{c.logged_by}</td>
                            <td className="p-1.5 text-right">{canEdit && <DeleteBtn onClick={() => removeCost(c.id)} testid={`cost-del-${c.id}`} />}</td>
                        </tr>))}
                    </tbody>
                </table>}
            </SectionCard>
            {showCost && <AddCostModal vesselId={vessel.id} onClose={() => setShowCost(false)} onSaved={() => { setShowCost(false); load(); onChanged?.(); }} />}
            {showBudget && <SetBudgetModal vesselId={vessel.id} existing={budget} onClose={() => setShowBudget(false)} onSaved={() => { setShowBudget(false); load(); onChanged?.(); }} />}
        </div>
    );
}
function Stat({ label, val, sub, accent, testid }) {
    return (
        <div className="bg-white border border-slate-200 rounded-sm p-3" data-testid={testid}>
            <div className="text-[9.5px] uppercase tracking-wider text-slate-500">{label}</div>
            <div className="font-display font-black text-base tabular leading-tight mt-0.5" style={{ color: accent }}>{val}</div>
            {sub && <div className="text-[10px] text-slate-400 mt-0.5">{sub}</div>}
        </div>
    );
}
const COST_CATS = ["fuel","breakdowns","manpower","claims","marine","plant_hire","maintenance","other"];
function AddCostModal({ vesselId, onClose, onSaved }) {
    const [f, setF] = useState({ category: "fuel", amount_zar: 0, description: "" });
    const submit = async () => {
        try { await api.post(`/vessels/${vesselId}/costs`, { ...f, amount_zar: parseFloat(f.amount_zar) || 0 }); toast.success("Cost added"); onSaved(); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    return (
        <InnerModal title="Add cost line" onClose={onClose}>
            <Field label="Category"><select className="g-input" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} data-testid="cost-cat">{COST_CATS.map((c) => <option key={c} value={c}>{c.replace("_"," ")}</option>)}</select></Field>
            <Field label="Amount (ZAR) *"><input type="number" min="0" step="0.01" className="g-input tabular" value={f.amount_zar} onChange={(e) => setF({ ...f, amount_zar: e.target.value })} data-testid="cost-amount" /></Field>
            <Field label="Description"><input className="g-input" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="Invoice / supplier / details" data-testid="cost-desc" /></Field>
            <Actions onClose={onClose} onSubmit={submit} testid="cost-submit" />
        </InnerModal>
    );
}
function SetBudgetModal({ vesselId, existing, onClose, onSaved }) {
    const [f, setF] = useState({
        total_budget_zar: existing?.total_budget_zar || 0,
        notes: existing?.notes || "",
        breakdown: existing?.breakdown || {},
    });
    const setCat = (k, v) => setF({ ...f, breakdown: { ...f.breakdown, [k]: parseFloat(v) || 0 } });
    const submit = async () => {
        try { await api.post(`/vessels/${vesselId}/budget`, { ...f, total_budget_zar: parseFloat(f.total_budget_zar) || 0 }); toast.success("Budget saved"); onSaved(); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    return (
        <InnerModal title="Set vessel budget" onClose={onClose}>
            <Field label="Total budget (ZAR) *"><input type="number" min="0" step="0.01" className="g-input tabular text-lg font-display font-bold" value={f.total_budget_zar} onChange={(e) => setF({ ...f, total_budget_zar: e.target.value })} data-testid="budget-total" /></Field>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-2 mb-1">Optional breakdown by category</div>
            <div className="grid grid-cols-2 gap-2">{COST_CATS.map((c) => (
                <Field key={c} label={c.replace("_"," ")}><input type="number" min="0" step="0.01" className="g-input tabular" value={f.breakdown[c] || 0} onChange={(e) => setCat(c, e.target.value)} /></Field>
            ))}</div>
            <Field label="Notes"><input className="g-input" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></Field>
            <Actions onClose={onClose} onSubmit={submit} testid="budget-submit" />
        </InnerModal>
    );
}

/* ------------------------------------------------ MANPOWER ------------------ */
function ManpowerTab({ vessel, onChanged, role }) {
    const canEdit = ["admin","manager"].includes(role);
    const [data, setData] = useState({ items: [], by_department: {}, total_headcount: 0, total_hours: 0 });
    const [showModal, setShowModal] = useState(false);
    const load = async () => { try { const r = await api.get(`/vessels/${vessel.id}/manpower`); setData(r.data); } catch (e) { toast.error(formatApiError(e)); } };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
    const removeRow = async (id) => {
        if (!window.confirm("Delete this manpower entry?")) return;
        try { await api.delete(`/vessels/${vessel.id}/manpower/${id}`); load(); onChanged?.(); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    return (
        <div className="space-y-3" data-testid="postop-manpower-pane">
            <div className="grid grid-cols-3 gap-2">
                {["electrical","mechanical","operations"].map((d) => (
                    <Stat key={d} label={d} val={`${data.by_department?.[d]?.headcount || 0} ppl`} sub={`${(data.by_department?.[d]?.hours || 0).toFixed(1)} h`} accent="#1E40AF" testid={`mp-stat-${d}`} />
                ))}
            </div>
            <SectionCard title="Manpower entries" accent="#1E40AF"
                action={canEdit && <button onClick={() => setShowModal(true)} data-testid="mp-add" className="px-2 py-1 rounded-sm bg-white text-[#1E40AF] hover:bg-blue-50 text-[10px] font-display font-bold uppercase tracking-wide inline-flex items-center gap-1"><Plus size={10} weight="bold" /> Add manpower</button>}>
                {data.items.length === 0 ? <div className="text-center py-6 text-slate-400 text-xs">No manpower logged yet.</div> :
                <table className="w-full text-xs tabular">
                    <thead className="text-slate-500 uppercase text-[10px]"><tr><th className="text-left p-1.5">Date</th><th className="text-left p-1.5">Dept</th><th className="text-right p-1.5">Headcount</th><th className="text-right p-1.5">Hours</th><th className="text-left p-1.5">Notes</th><th className="text-left p-1.5">By</th><th className="p-1.5"></th></tr></thead>
                    <tbody>{data.items.map((m) => (
                        <tr key={m.id} className="border-t border-slate-100" data-testid={`mp-row-${m.id}`}>
                            <td className="p-1.5">{m.shift_date || fmtDate(m.created_at)}</td>
                            <td className="p-1.5 capitalize">{m.department}</td>
                            <td className="p-1.5 text-right font-semibold">{m.headcount}</td>
                            <td className="p-1.5 text-right">{(m.hours || 0).toFixed(1)}</td>
                            <td className="p-1.5 max-w-xs truncate" title={m.notes}>{m.notes || "—"}</td>
                            <td className="p-1.5 text-slate-500">{m.logged_by}</td>
                            <td className="p-1.5 text-right">{canEdit && <DeleteBtn onClick={() => removeRow(m.id)} testid={`mp-del-${m.id}`} />}</td>
                        </tr>))}
                    </tbody>
                </table>}
            </SectionCard>
            {showModal && <AddManpowerModal vesselId={vessel.id} onClose={() => setShowModal(false)} onSaved={() => { setShowModal(false); load(); onChanged?.(); }} />}
        </div>
    );
}
function AddManpowerModal({ vesselId, onClose, onSaved }) {
    const [f, setF] = useState({ department: "operations", headcount: 1, hours: 0, shift_date: new Date().toISOString().slice(0,10), notes: "" });
    const submit = async () => {
        try { await api.post(`/vessels/${vesselId}/manpower`, { ...f, headcount: parseInt(f.headcount)||0, hours: parseFloat(f.hours)||0 }); toast.success("Manpower added"); onSaved(); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    return (
        <InnerModal title="Add manpower" onClose={onClose}>
            <Field label="Department *"><select className="g-input" value={f.department} onChange={(e) => setF({ ...f, department: e.target.value })} data-testid="mp-dept"><option value="electrical">Electrical</option><option value="mechanical">Mechanical</option><option value="operations">Operations</option></select></Field>
            <div className="grid grid-cols-2 gap-2">
                <Field label="Headcount *"><input type="number" min="0" step="1" className="g-input tabular" value={f.headcount} onChange={(e) => setF({ ...f, headcount: e.target.value })} data-testid="mp-headcount" /></Field>
                <Field label="Hours *"><input type="number" min="0" step="0.5" className="g-input tabular" value={f.hours} onChange={(e) => setF({ ...f, hours: e.target.value })} data-testid="mp-hours" /></Field>
            </div>
            <Field label="Shift date"><input type="date" className="g-input" value={f.shift_date} onChange={(e) => setF({ ...f, shift_date: e.target.value })} /></Field>
            <Field label="Notes"><input className="g-input" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></Field>
            <Actions onClose={onClose} onSubmit={submit} testid="mp-submit" />
        </InnerModal>
    );
}

/* ------------------------------------------------ FLEET ALLOCATIONS --------- */
function FleetTab({ vessel, onChanged, role }) {
    const canEdit = ["admin","fleet_manager"].includes(role);
    const [data, setData] = useState({ items: [], machine_count: 0, manual_count: 0, auto_count: 0 });
    const [machines, setMachines] = useState([]);
    const [machineId, setMachineId] = useState("");
    const [notes, setNotes] = useState("");
    const load = async () => {
        try {
            const [a, m] = await Promise.all([
                api.get(`/vessels/${vessel.id}/fleet-allocations`),
                api.get("/fel/machines"),
            ]);
            setData(a.data); setMachines(m.data || []);
        } catch (e) { toast.error(formatApiError(e)); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
    const add = async () => {
        if (!machineId) return toast.error("Pick a machine");
        try { await api.post(`/vessels/${vessel.id}/fleet-allocations`, { machine_id: machineId, notes: notes || null }); toast.success("Machine allocated"); setMachineId(""); setNotes(""); load(); onChanged?.(); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    const removeRow = async (id) => {
        if (id.startsWith("auto-")) return toast.info("Auto-derived (from FEL session) — cannot be removed");
        if (!window.confirm("Remove this allocation?")) return;
        try { await api.delete(`/vessels/${vessel.id}/fleet-allocations/${id}`); load(); onChanged?.(); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    return (
        <div className="space-y-3" data-testid="postop-fleet-pane">
            <div className="grid grid-cols-3 gap-2">
                <Stat label="Total machines" val={data.machine_count} accent="var(--grindrod-navy)" testid="fl-stat-total" />
                <Stat label="Manually assigned" val={data.manual_count} accent="#1E40AF" testid="fl-stat-manual" />
                <Stat label="From FEL sessions" val={data.auto_count} accent="var(--grindrod-green)" testid="fl-stat-auto" />
            </div>
            {canEdit && (
                <SectionCard title="Assign machine to vessel" accent="var(--grindrod-navy)">
                    <div className="grid sm:grid-cols-[1fr_1fr_auto] gap-2">
                        <select className="g-input text-sm" value={machineId} onChange={(e) => setMachineId(e.target.value)} data-testid="fl-machine-select">
                            <option value="">— pick a machine —</option>
                            {machines.filter((m) => m.in_service !== false).map((m) => (
                                <option key={m.id} value={m.id}>{m.machine_number}{m.make || m.model ? ` · ${[m.make, m.model].filter(Boolean).join(" ")}` : ""}</option>
                            ))}
                        </select>
                        <input className="g-input text-sm" placeholder="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="fl-notes" />
                        <button onClick={add} className="btn-primary text-xs inline-flex items-center gap-1" data-testid="fl-add"><Plus size={12} weight="bold" /> Assign</button>
                    </div>
                </SectionCard>
            )}
            <SectionCard title="Allocated machines" accent="var(--grindrod-navy)">
                {data.items.length === 0 ? <div className="text-center py-6 text-slate-400 text-xs">No machines allocated yet.</div> :
                <table className="w-full text-xs tabular">
                    <thead className="text-slate-500 uppercase text-[10px]"><tr><th className="text-left p-1.5">Machine</th><th className="text-left p-1.5">Make / Model</th><th className="text-left p-1.5">Source</th><th className="text-left p-1.5">Assigned by</th><th className="text-left p-1.5">When</th><th className="p-1.5"></th></tr></thead>
                    <tbody>{data.items.map((a) => (
                        <tr key={a.id} className="border-t border-slate-100" data-testid={`fl-row-${a.id}`}>
                            <td className="p-1.5 font-display font-bold text-[var(--grindrod-navy)]">{a.machine_number}</td>
                            <td className="p-1.5 text-slate-600">{[a.make, a.model].filter(Boolean).join(" ") || "—"}</td>
                            <td className="p-1.5"><span className={`g-pill ${a.source === "manual" ? "g-pill-navy" : "g-pill-green"}`}>{a.source}</span></td>
                            <td className="p-1.5 text-slate-500">{a.assigned_by || "—"}</td>
                            <td className="p-1.5 text-slate-500">{fmtDate(a.assigned_at)}</td>
                            <td className="p-1.5 text-right">{canEdit && a.source === "manual" && <DeleteBtn onClick={() => removeRow(a.id)} testid={`fl-del-${a.id}`} />}</td>
                        </tr>))}
                    </tbody>
                </table>}
            </SectionCard>
        </div>
    );
}

/* ------------------------------------------------ SAFETY ------------------ */
function SafetyTab({ vessel, onChanged, role }) {
    const canEdit = ["admin","safety"].includes(role);
    const [items, setItems] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const load = async () => { try { const r = await api.get("/safety/incidents", { params: { vessel_id: vessel.id } }); setItems(r.data || []); } catch (e) { toast.error(formatApiError(e)); } };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
    const removeRow = async (id) => {
        if (!window.confirm("Delete this safety incident?")) return;
        try { await api.delete(`/safety/incidents/${id}`); load(); onChanged?.(); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    const totalLost = items.reduce((s, i) => s + (parseFloat(i.hours_lost) || 0), 0);
    return (
        <div className="space-y-3" data-testid="postop-safety-pane">
            <div className="grid grid-cols-2 gap-2">
                <Stat label="Incidents on this vessel" val={items.length} accent="var(--grindrod-red)" testid="sf-stat-count" />
                <Stat label="Total hours lost" val={`${totalLost.toFixed(1)} h`} accent="#B36B00" testid="sf-stat-hours" />
            </div>
            <SectionCard title="Safety incidents" accent="#00A344"
                action={canEdit && <button onClick={() => setShowModal(true)} data-testid="sf-add" className="px-2 py-1 rounded-sm bg-white text-[#00A344] hover:bg-emerald-50 text-[10px] font-display font-bold uppercase tracking-wide inline-flex items-center gap-1"><Plus size={10} weight="bold" /> Log incident</button>}>
                {items.length === 0 ? <div className="text-center py-6 text-slate-400 text-xs">No safety incidents on this vessel.</div> :
                <table className="w-full text-xs"><thead className="text-slate-500 uppercase text-[10px]"><tr><th className="text-left p-1.5">When</th><th className="text-left p-1.5">Type</th><th className="text-left p-1.5">Description</th><th className="text-right p-1.5">Hrs lost</th><th className="text-left p-1.5">By</th><th className="p-1.5"></th></tr></thead>
                    <tbody>{items.map((i) => (
                        <tr key={i.id} className="border-t border-slate-100" data-testid={`sf-row-${i.id}`}>
                            <td className="p-1.5">{fmtDate(i.occurred_at)}</td>
                            <td className="p-1.5 capitalize">{(i.incident_type || "").replace(/_/g," ")}</td>
                            <td className="p-1.5 max-w-md truncate" title={i.description}>{i.description}</td>
                            <td className="p-1.5 text-right tabular">{Number(i.hours_lost || 0).toFixed(1)}</td>
                            <td className="p-1.5 text-slate-500">{i.logged_by}</td>
                            <td className="p-1.5 text-right">{canEdit && <DeleteBtn onClick={() => removeRow(i.id)} testid={`sf-del-${i.id}`} />}</td>
                        </tr>))}
                    </tbody>
                </table>}
            </SectionCard>
            {showModal && <AddSafetyModal vesselId={vessel.id} onClose={() => setShowModal(false)} onSaved={() => { setShowModal(false); load(); onChanged?.(); }} />}
        </div>
    );
}
function AddSafetyModal({ vesselId, onClose, onSaved }) {
    const [f, setF] = useState({ incident_type: "near_miss", description: "", hours_lost: 0, operation: "rb_vessel" });
    const submit = async () => {
        try { await api.post("/safety/incidents", { ...f, vessel_id: vesselId, hours_lost: parseFloat(f.hours_lost)||0 }); toast.success("Incident logged"); onSaved(); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    return (
        <InnerModal title="Log safety incident" onClose={onClose}>
            <Field label="Type *"><select className="g-input" value={f.incident_type} onChange={(e) => setF({ ...f, incident_type: e.target.value })} data-testid="sf-type">
                <option value="near_miss">Near miss</option><option value="first_aid">First aid</option>
                <option value="medical">Medical treatment</option><option value="lost_time">Lost time injury (LTIFR)</option>
                <option value="property_damage">Property damage</option>
            </select></Field>
            <Field label="Description *"><textarea rows={3} className="g-input" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="sf-desc" /></Field>
            <Field label="Hours lost (time lost due to injury)"><input type="number" min="0" step="0.5" className="g-input tabular" value={f.hours_lost} onChange={(e) => setF({ ...f, hours_lost: e.target.value })} data-testid="sf-hours" /></Field>
            <Actions onClose={onClose} onSubmit={submit} disabled={!f.description.trim()} testid="sf-submit" />
        </InnerModal>
    );
}

/* ------------------------------------------------ CLIENT FEEDBACK --------- */
function ClientTab({ vessel, onChanged, role }) {
    const canEdit = ["admin","client_liaison"].includes(role);
    const [data, setData] = useState({ items: [], counts: { complaint: 0, recommendation: 0, positive: 0 } });
    const [showModal, setShowModal] = useState(null); // 'complaint'|'recommendation'|'positive'
    const load = async () => { try { const r = await api.get(`/vessels/${vessel.id}/client-feedback`); setData(r.data); } catch (e) { toast.error(formatApiError(e)); } };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
    const removeRow = async (id) => {
        if (!window.confirm("Delete this feedback entry?")) return;
        try { await api.delete(`/vessels/${vessel.id}/client-feedback/${id}`); load(); onChanged?.(); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    const pillFor = (t) => ({ complaint: "g-pill-red", recommendation: "g-pill-amber", positive: "g-pill-green" }[t] || "");
    const iconFor = (t) => t === "positive" ? ThumbsUp : t === "recommendation" ? ChatTeardropDots : Warning;
    return (
        <div className="space-y-3" data-testid="postop-client-pane">
            <div className="grid grid-cols-3 gap-2">
                <Stat label="Complaints"      val={data.counts.complaint}      accent="var(--grindrod-red)"   testid="cl-stat-complaints" />
                <Stat label="Recommendations" val={data.counts.recommendation} accent="#B36B00"               testid="cl-stat-recs" />
                <Stat label="Positive"        val={data.counts.positive}       accent="var(--grindrod-green)" testid="cl-stat-positive" />
            </div>
            {canEdit && (
                <div className="flex flex-wrap gap-2">
                    <button onClick={() => setShowModal("complaint")} data-testid="cl-add-complaint" className="btn-outline !border-[var(--grindrod-red)] !text-[var(--grindrod-red)] text-xs inline-flex items-center gap-1"><Plus size={11} weight="bold" /> Log complaint</button>
                    <button onClick={() => setShowModal("recommendation")} data-testid="cl-add-rec" className="btn-outline text-xs inline-flex items-center gap-1"><Plus size={11} weight="bold" /> Add recommendation</button>
                    <button onClick={() => setShowModal("positive")} data-testid="cl-add-positive" className="btn-outline !border-[var(--grindrod-green)] !text-[var(--grindrod-green)] text-xs inline-flex items-center gap-1"><Plus size={11} weight="bold" /> Add positive feedback</button>
                </div>
            )}
            <SectionCard title="Feedback log" accent="#7C3AED">
                {data.items.length === 0 ? <div className="text-center py-6 text-slate-400 text-xs">No client feedback recorded yet.</div> :
                <div className="space-y-2">{data.items.map((f) => {
                    const Icon = iconFor(f.feedback_type);
                    return (
                        <div key={f.id} className="rounded-sm border border-slate-200 p-2.5" data-testid={`cl-row-${f.id}`}>
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                                <span className={`g-pill ${pillFor(f.feedback_type)} inline-flex items-center gap-1`}><Icon size={10} weight="fill" /> {f.feedback_type}</span>
                                {f.category && <span className="text-[10px] uppercase tracking-wider text-slate-500">· {f.category}</span>}
                                <span className="text-[10px] text-slate-400 ml-auto">{fmtDate(f.created_at)} · {f.logged_by}</span>
                                {canEdit && <DeleteBtn onClick={() => removeRow(f.id)} testid={`cl-del-${f.id}`} />}
                            </div>
                            <div className="text-xs text-slate-700 leading-snug whitespace-pre-line">{f.description}</div>
                        </div>);
                })}</div>}
            </SectionCard>
            {showModal && <AddClientFeedbackModal vesselId={vessel.id} type={showModal} clientName={vessel.client_name} onClose={() => setShowModal(null)} onSaved={() => { setShowModal(null); load(); onChanged?.(); }} />}
        </div>
    );
}
function AddClientFeedbackModal({ vesselId, type, clientName, onClose, onSaved }) {
    const [f, setF] = useState({ feedback_type: type, category: "", description: "", client_name: clientName || "" });
    const submit = async () => {
        try { await api.post(`/vessels/${vesselId}/client-feedback`, { ...f, category: f.category || null }); toast.success("Feedback logged"); onSaved(); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    const title = { complaint: "Log client complaint", recommendation: "Log client recommendation", positive: "Log positive feedback" }[type];
    return (
        <InnerModal title={title} onClose={onClose}>
            <Field label="Client"><input className="g-input" value={f.client_name} onChange={(e) => setF({ ...f, client_name: e.target.value })} data-testid="cl-client" /></Field>
            <Field label="Category (optional)"><input className="g-input" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} placeholder="e.g. delays, documentation, service quality" data-testid="cl-category" /></Field>
            <Field label="Description *"><textarea rows={4} className="g-input" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="cl-desc" /></Field>
            <Actions onClose={onClose} onSubmit={submit} disabled={!f.description.trim()} testid="cl-submit" />
        </InnerModal>
    );
}

/* ------------------------------------------------ DELAY FEEDBACK --------- */
function DelayFeedbackTab({ vessel, onChanged, role }) {
    const canEdit = ["admin","manager"].includes(role);
    const [delays, setDelays] = useState([]);
    const [editing, setEditing] = useState(null);
    const [text, setText] = useState("");
    const load = async () => {
        try { const r = await api.get(`/vessels/${vessel.id}/full-report`); setDelays(r.data.delays || []); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
    const save = async (id) => {
        if (!text.trim()) return toast.error("Feedback cannot be empty");
        try { await api.patch(`/vessels/${vessel.id}/delays/${id}/feedback`, { feedback: text.trim() }); toast.success("Feedback saved"); setEditing(null); setText(""); load(); onChanged?.(); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    const clear = async (id) => {
        if (!window.confirm("Remove this feedback?")) return;
        try { await api.delete(`/vessels/${vessel.id}/delays/${id}/feedback`); load(); onChanged?.(); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    const sorted = [...delays].sort((a, b) => (b.duration_minutes || 0) - (a.duration_minutes || 0));
    return (
        <div className="space-y-3" data-testid="postop-delays-pane">
            <SectionCard title="Delays & breakdowns — leave management feedback" accent="var(--grindrod-red)">
                {sorted.length === 0 ? <div className="text-center py-6 text-slate-400 text-xs">No delays recorded yet.</div> :
                <div className="space-y-2">{sorted.map((d, idx) => (
                    <div key={d.id} className="rounded-sm border border-slate-200 p-2.5" data-testid={`df-row-${d.id}`}>
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                            {idx < 3 && <span className="g-pill g-pill-red">Top delay #{idx + 1}</span>}
                            <span className="font-display font-bold text-[12px] text-[var(--grindrod-navy)]">{d.reason || "Delay"}</span>
                            <span className="text-[10px] text-slate-500">· {fmtDuration(d.duration_minutes || 0)} · {d.source || "manual"}</span>
                            <span className="ml-auto text-[10px] text-slate-400">{fmtDate(d.start_time)}</span>
                        </div>
                        {d.actions_taken && <div className="text-[11px] text-slate-600 mb-1.5 italic">"{d.actions_taken}"</div>}
                        {d.manager_feedback ? (
                            <div className="bg-amber-50 border-l-2 border-amber-400 p-2 rounded-sm" data-testid={`df-fb-${d.id}`}>
                                <div className="text-[10px] uppercase tracking-wider text-amber-700 font-bold mb-0.5">Management feedback · {d.manager_feedback_by}</div>
                                <div className="text-xs text-slate-700 whitespace-pre-line">{d.manager_feedback}</div>
                                {canEdit && (
                                    <div className="flex gap-2 mt-1.5">
                                        <button onClick={() => { setEditing(d.id); setText(d.manager_feedback); }} className="text-[10px] underline text-slate-500" data-testid={`df-edit-${d.id}`}>Edit</button>
                                        <button onClick={() => clear(d.id)} className="text-[10px] underline text-[var(--grindrod-red)]" data-testid={`df-clear-${d.id}`}>Remove</button>
                                    </div>
                                )}
                            </div>
                        ) : canEdit && editing !== d.id ? (
                            <button onClick={() => { setEditing(d.id); setText(""); }} className="text-[11px] btn-outline !py-1 !px-2" data-testid={`df-add-${d.id}`}>+ Add management feedback</button>
                        ) : null}
                        {canEdit && editing === d.id && (
                            <div className="mt-2 space-y-1.5">
                                <textarea rows={2} className="g-input text-xs" placeholder="e.g. Root cause was..." value={text} onChange={(e) => setText(e.target.value)} data-testid={`df-input-${d.id}`} />
                                <div className="flex gap-2 justify-end">
                                    <button onClick={() => { setEditing(null); setText(""); }} className="btn-outline !py-1 !px-2 text-[10px]">Cancel</button>
                                    <button onClick={() => save(d.id)} className="btn-primary !py-1 !px-2 text-[10px]" data-testid={`df-save-${d.id}`}>Save feedback</button>
                                </div>
                            </div>
                        )}
                    </div>))}</div>}
            </SectionCard>
        </div>
    );
}

/* ------------------------------------------------ LITTLE HELPERS ----------- */
function InnerModal({ title, onClose, children }) {
    return (
        <div className="fixed inset-0 z-[120] bg-black/60 flex items-center justify-center p-3" onClick={onClose}>
            <div className="bg-white w-full max-w-md max-h-[90vh] overflow-y-auto rounded-sm shadow-2xl" onClick={(e) => e.stopPropagation()}>
                <div className="bg-[var(--grindrod-navy)] text-white px-4 py-2 font-display font-black uppercase tracking-wide text-sm flex items-center">
                    <div className="flex-1">{title}</div>
                    <button onClick={onClose} className="text-white"><X size={14} /></button>
                </div>
                <div className="p-3 space-y-2">{children}</div>
            </div>
        </div>
    );
}
function Field({ label, children }) {
    return <div><label className="uppercase-label block mb-1">{label}</label>{children}</div>;
}
function Actions({ onClose, onSubmit, disabled, testid }) {
    return (
        <div className="flex gap-2 justify-end pt-2">
            <button onClick={onClose} className="btn-outline">Cancel</button>
            <button onClick={onSubmit} disabled={disabled} className="btn-primary" data-testid={testid}>Save</button>
        </div>
    );
}
